
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Edit, Eye, LayoutList } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FreeResponseDisplay } from "./free-response/FreeResponseDisplay";
import { QuizDisplay } from "./QuizDisplay";
import { DragDropGame } from "./DragDropGame";
import { LinkingGame } from "./LinkingGame";
import { ResourceFile } from "./types";
import {
  isPDF,
  isVideo,
  isQuiz,
  isFreeResponse,
  isDragDrop,
  isLinking
} from "./utils/fileTypeUtils";

interface PDFSidebarActionsProps {
  file: ResourceFile;
  resourceId: string;
  primaryColor?: string;
  secondaryColor?: string;
  onShowThumbnails?: () => void;
  currentFile?: ResourceFile;
  topic?: string;
  company?: string;
  onCompletedQuiz?: () => void;
  hasNextFile?: boolean;
  onNextFile?: (() => void) | undefined;
}

const PDFSidebarActions = ({
  file,
  resourceId,
  primaryColor,
  secondaryColor,
  onShowThumbnails,
  currentFile,
  topic,
  company,
  onCompletedQuiz,
  hasNextFile,
  onNextFile,
}: PDFSidebarActionsProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  
  // Use either file or currentFile based on what's provided
  const displayFile = currentFile || file;

  return (
    <div className="space-y-4">
      {isPDF(displayFile.file_url) && (
        <>
          {onShowThumbnails && (
            <Button
              variant="outline"
              className="w-full"
              onClick={onShowThumbnails}
            >
              <LayoutList className="mr-2 h-4 w-4" />
              Show Thumbnails
            </Button>
          )}
          {primaryColor && secondaryColor && (
            <div className="grid grid-cols-2 gap-2">
              <div
                className="h-12 w-full rounded-md"
                style={{ backgroundColor: primaryColor }}
              />
              <div
                className="h-12 w-full rounded-md"
                style={{ backgroundColor: secondaryColor }}
              />
            </div>
          )}
        </>
      )}

      {isQuiz(displayFile) && displayFile.quiz_id && (
        <>
          <QuizDisplay
            quiz={null}
            isLoading={false}
            isAccessible={true}
            progress={100}
            quizId={displayFile.quiz_id}
            resourceId={resourceId}
            onComplete={onCompletedQuiz}
            isPreview={true}
          />
        </>
      )}

      {isFreeResponse(displayFile) && (
        <>
          <FreeResponseDisplay
            fileId={displayFile.id}
            resourceId={resourceId}
            isPreview={true}
          />
        </>
      )}

      {isDragDrop(displayFile) && (
        <>
          <DragDropGame
            fileId={displayFile.id}
            resourceId={resourceId}
            isPreview={true}
          />
        </>
      )}
      
      {isLinking(displayFile) && (
        <>
          <LinkingGame
            fileId={displayFile.id}
            resourceId={resourceId}
            isPreview={true}
          />
        </>
      )}
      
      {isVideo(displayFile.file_url) && displayFile.file_url && (
        <>
          <div className="border rounded-md p-4">
            <p className="text-sm text-muted-foreground">
              This is a video file.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PDFSidebarActions;
